import {
  Box,
  useTheme,
  useMediaQuery,
  Button,
  Paper,
  Typography,
  Chip,
} from '@mui/material';
import { MailOutline } from '@mui/icons-material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useRoutePersistence } from '@hooks/useRoutePersistence';
import { clearRouteHistory } from '@services/routePersistenceService';
import { PrivateRoutes } from '@enums/privateRouteEnums';
import LoanProgress from './LoanProgress';
import BudgetGridEditView from './BudgetGridEditView';
import BudgetGridReadOnlyView from './BudgetGridReadOnlyView';
import { BudgetEditItem } from '@models/drawRequests/budgetEditItem';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDrawRequestQueryApi } from '@hooks/api/useDrawRequestQueryApi';
import { useDrawRequestCommandApi } from '@hooks/api/useDrawRequestCommandApi';
import { DrawRequestDetailEditRequestModel } from '@models/drawRequests/drawRequestDetailEditRequestModel';
import FileSelector from '@components/sharedComponents/FileSelector';
import { useAttachmentApi, fileToBase64 } from '@hooks/api/useAttachmentApi';
import { getStatusChipColor } from './DrawRequestGridColumns';
import {
  DRAW_MANAGER_TAB_REC_ID,
  DRAW_REQUEST_STATUS,
} from '@constants/drawManagerConstants';
import {
  ACCEPTED_FILE_EXTENSIONS,
  MAX_FILE_COUNT,
  MAX_FILE_SIZE_MB,
} from '@constants/fileTypeConstants';
import { AttOwnerType } from '@enums/attOwnerTypes';
import { DocumentTypes } from '@enums/documentTypes';
import { DrawManagerAttachmentRequestModel } from '@models/drawRequests/drawManagerAttachmentRequestModel';
import { showCustomErrorToast } from '@utils/customToast';
import { colors } from '@theme/colors';
import DrawRequestMessage from './DrawRequestMessage';
import { useDrawRequestMessageApi } from '@hooks/api/useDrawRequestMessageApi';
import { useDrawRequestMessageHub } from '@hooks/useDrawRequestMessageHub';
import { useApp } from '@context/AppContext';
import { OriginatorTypes } from '@enums/originatorTypes';
import { RecipientTypes } from '@enums/recipientTypes';
import { usePostHog } from '@posthog/react';

export default function ReviewDraw() {
  const theme = useTheme();
  const { isSmallDesktop } = useApp();
  const posthog = usePostHog();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const navigate = useNavigate();
  const location = useLocation();
  const { account, sourceRecId, drawRequestId } = useParams<{
    account: string;
    sourceRecId: string;
    drawRequestId: string;
  }>();
  const { setIntentionalPath } = useRoutePersistence();
  const [budgetItems, setBudgetItems] = useState<BudgetEditItem[]>([]);
  const [originalBudgetItems, setOriginalBudgetItems] = useState<
    BudgetEditItem[]
  >([]);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [hasBudgetChanges, setHasBudgetChanges] = useState(false);
  const [drawStatus, setDrawStatus] = useState<number>(DRAW_REQUEST_STATUS.NEW);

  const addAttachmentMutation = useAttachmentApi.useAddAttachment();
  const deleteAttachmentMutation = useAttachmentApi.useDeleteAttachment();
  const updateDrawRequestMutation =
    useDrawRequestCommandApi.useUpdateDrawRequestBudgetItems();
  const markMessagesAsReadMutation = useDrawRequestMessageApi.useMarkAsRead();

  useDrawRequestMessageHub({
    drawRequestId: drawRequestId || '',
    sourceRecId: sourceRecId || '',
    account: account || '',
  });

  const { data: messagesData, refetch: refetchMessages } =
    useDrawRequestMessageApi.useFetchMessagesInfinite(
      drawRequestId || '',
      sourceRecId || ''
    );

  const { data: unreadCount } = useDrawRequestMessageApi.useFetchUnreadCount(
    drawRequestId || '',
    sourceRecId || '',
    !!drawRequestId && !!sourceRecId
  );

  const messages = messagesData?.pages.flat() || [];

  const prevPathnameRef = useRef<string | null>(null);
  useEffect(() => {
    if (drawRequestId && sourceRecId) {
      const currentPath = location.pathname;
      const wasOnMessagePage = prevPathnameRef.current?.includes(
        'draw-request-message'
      );
      const isOnReviewPage = currentPath.includes('review-draw');

      if (wasOnMessagePage && isOnReviewPage) {
        refetchMessages();
      }

      prevPathnameRef.current = currentPath;
    }
  }, [location.pathname, drawRequestId, sourceRecId, refetchMessages]);

  const hasUnreadMessages = useMemo(() => {
    if (unreadCount !== undefined) {
      return unreadCount > 0;
    }
    return messages.some(
      msg =>
        msg.originatorType === OriginatorTypes.Company &&
        msg.recipientType === RecipientTypes.Borrower &&
        !msg.readDateTime
    );
  }, [unreadCount, messages]);

  const {
    data: drawRequestDetails,
    isLoading: isLoadingDrawRequest,
    error: drawRequestError,
  } = useDrawRequestQueryApi.useFetchDrawRequestDetails(
    drawRequestId || '',
    sourceRecId || ''
  );

  const { data: attachmentsData, isLoading: isLoadingAttachments } =
    useAttachmentApi.useGetAttachmentsByDrawRequest(
      drawRequestId || '',
      sourceRecId || '',
      !!drawRequestId
    );

  const handleBudgetItemsChange = (items: BudgetEditItem[]) => {
    setBudgetItems(items);
  };

  const drawTotal = budgetItems.reduce(
    (total, item) => total + (item.requestAmount || 0),
    0
  );

  const handleValidationChange = (isValid: boolean) => {
    setIsFormValid(isValid);
  };

  const isDisbursed = drawStatus === DRAW_REQUEST_STATUS.DISBURSED;
  const isCancelled = drawStatus === DRAW_REQUEST_STATUS.CANCELLED;
  const isReadOnly = isDisbursed || isCancelled;
  const canEditBudget = !isReadOnly;
  const canDeleteAttachments =
    drawStatus === DRAW_REQUEST_STATUS.NEW && !isReadOnly;

  const compareBudgetItems = (
    original: BudgetEditItem[],
    current: BudgetEditItem[]
  ): boolean => {
    if (original.length !== current.length) return true;

    return original.some((origItem, index) => {
      const currItem = current[index];
      return (
        origItem.budgetItem !== currItem.budgetItem ||
        origItem.remainingAmount !== currItem.remainingAmount ||
        origItem.requestAmount !== currItem.requestAmount ||
        origItem.percentComplete !== currItem.percentComplete
      );
    });
  };

  useEffect(() => {
    const budgetChanged = compareBudgetItems(originalBudgetItems, budgetItems);
    setHasBudgetChanges(budgetChanged);
  }, [budgetItems, originalBudgetItems, canEditBudget]);

  useEffect(() => {
    if (
      drawRequestDetails?.drawRequestDetails &&
      Array.isArray(drawRequestDetails?.drawRequestDetails)
    ) {
      const convertedBudgetItems: BudgetEditItem[] =
        drawRequestDetails?.drawRequestDetails.map(detail => ({
          id: detail.recId,
          budgetItem: detail.budgetItemRecId,
          remainingAmount: detail.remainingBudget || 0,
          requestAmount: detail.drawAmount || 0,
          percentComplete: detail.pctCompleted || 0,
          isNew: false,
        }));

      setBudgetItems(convertedBudgetItems);
      setOriginalBudgetItems(convertedBudgetItems);
    }
  }, [drawRequestDetails?.drawRequestDetails]);

  useEffect(() => {
    if (drawRequestDetails?.status !== undefined) {
      setDrawStatus(drawRequestDetails.status);
    }
  }, [drawRequestDetails]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [drawRequestId]);

  useEffect(() => {
    if (attachmentsData && attachmentsData.length > 0) {
      const existingFiles: File[] = attachmentsData.map(attachment => {
        const file = new File([''], attachment.fileName || 'Unknown', {
          type: 'application/octet-stream',
        });

        (file as any).attachmentId = attachment.recId;
        (file as any).isExistingAttachment = true;
        (file as any).description =
          attachment.description || attachment.fileName;
        return file;
      });

      setSelectedFiles(existingFiles);
    }
  }, [attachmentsData]);

  const handleAttachmentAdd = async (file: File) => {
    if (!sourceRecId || !drawRequestId || !drawRequestDetails?.drawRequestId)
      return;

    try {
      const fileContent = await fileToBase64(file);
      const attachmentRequest: DrawManagerAttachmentRequestModel = {
        OwnerRecId: sourceRecId,
        OwnerType: AttOwnerType.Borrower,
        DrawRequestRecId: drawRequestId,
        FileName: file.name,
        Document: fileContent,
        DocType: DocumentTypes.Other,
        Description: `${drawRequestDetails.drawRequestId}_${file.name}`,
        TabRecId: DRAW_MANAGER_TAB_REC_ID,
        Publish: true,
        SourceRecId: sourceRecId,
      };

      await addAttachmentMutation.mutateAsync(attachmentRequest);
    } catch (error) {
      showCustomErrorToast({ message: error as string });
    }
  };

  const handleAttachmentDelete = async (attachmentId: string) => {
    if (!drawRequestId || !canDeleteAttachments) return;

    try {
      await deleteAttachmentMutation.mutateAsync({
        recId: attachmentId,
        drawRequestRecId: drawRequestId,
        sourceRecId: sourceRecId || '',
      });
    } catch (error) {
      showCustomErrorToast({ message: error as string });
    }
  };

  const handleFilesChangeWithRealtime = async (files: File[]) => {
    const previousFiles = selectedFiles;
    setSelectedFiles(files);

    const newFiles = files.filter(
      file =>
        !previousFiles.some(
          prevFile =>
            (file as any).attachmentId === (prevFile as any).attachmentId &&
            file.name === prevFile.name
        )
    );

    const removedFiles = previousFiles.filter(prevFile => {
      const prevId = (prevFile as any).attachmentId;
      return (
        prevId && !files.some(file => (file as any).attachmentId === prevId)
      );
    });

    for (const file of newFiles) {
      if (!(file as any).isExistingAttachment) {
        await handleAttachmentAdd(file);
      }
    }

    for (const file of removedFiles) {
      const attachmentId = (file as any).attachmentId;
      if (attachmentId) {
        await handleAttachmentDelete(attachmentId);
      }
    }
  };

  const handleSubmitRequest = async () => {
    if (!sourceRecId || !isFormValid || !hasBudgetChanges || !drawRequestId)
      return;

    try {
      const drawRequestDetails: DrawRequestDetailEditRequestModel[] =
        budgetItems
          .filter(item => item.budgetItem && item.requestAmount > 0)
          .map(item => ({
            recId: item.id || undefined,
            drawRequestRecId: drawRequestId,
            budgetItemRecId: item.budgetItem,
            drawAmount: item.requestAmount,
            pctCompleted: item.percentComplete || 0,
          }));

      const request = {
        request: drawRequestDetails,
        sourceRecId,
      };
      await updateDrawRequestMutation.mutateAsync(request);

      posthog?.capture('draw_request_updated', {
        draw_request_id: drawRequestId,
        source_rec_id: sourceRecId,
        loan_account: account,
        draw_total: drawTotal,
        budget_item_count: drawRequestDetails.length,
      });

      handleBackClick();
    } catch (error) {
      showCustomErrorToast({ message: error as string });
      posthog?.captureException(error as Error, {
        extra: { draw_request_id: drawRequestId, source_rec_id: sourceRecId },
      });
    }
  };

  const renderLoanProgressPaper = () => (
    <Paper
      elevation={0}
      sx={{
        p: '22px 18px',
        backgroundColor: theme.palette.background.paper,
        width: '100%',
      }}
      component="section"
      aria-label="Loan progress section"
    >
      <LoanProgress
        sourceRecId={sourceRecId || ''}
        budgetItems={budgetItems}
        isDisbursed={isDisbursed}
      />
    </Paper>
  );

  const renderBudgetSection = () => (
    <Paper
      elevation={0}
      sx={{
        p: '22px 18px',
        backgroundColor: theme.palette.background.paper,
        width: '100%',
        mb: isMobile ? 3 : 0,
      }}
      component="section"
      aria-label="Budget details section"
    >
      {isReadOnly ? (
        <BudgetGridReadOnlyView
          drawDetails={drawRequestDetails?.drawRequestDetails || []}
          loanRecId={sourceRecId || ''}
          title="Budget Details"
        />
      ) : (
        <BudgetGridEditView
          sourceRecId={sourceRecId || ''}
          initialItems={budgetItems}
          onItemsChange={handleBudgetItemsChange}
          onValidationChange={handleValidationChange}
          buttonText="Update Draw Request"
          submittingText="Updating..."
        />
      )}
    </Paper>
  );

  const renderMobileLayout = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
      role="region"
      aria-label="Mobile layout content"
    >
      {renderLoanProgressPaper()}
      {renderBudgetSection()}
    </Box>
  );

  const renderUpdateButton = () => (
    <Button
      onClick={
        isFormValid && hasBudgetChanges ? handleSubmitRequest : undefined
      }
      disabled={
        !isFormValid ||
        !hasBudgetChanges ||
        updateDrawRequestMutation.isPending ||
        addAttachmentMutation.isPending ||
        deleteAttachmentMutation.isPending
      }
      aria-label="Update draw request with budget changes"
      sx={{
        backgroundColor:
          isFormValid && hasBudgetChanges
            ? theme.palette.primary.main
            : theme.palette.grey[400],
        color: theme.palette.primary.contrastText,
        px: 4,
        py: 2,
        textTransform: 'none',
        fontWeight: 'bold',
        opacity: isFormValid && hasBudgetChanges ? 1 : 0.6,
        whiteSpace: 'nowrap',
        minWidth: 'fit-content',
        flexShrink: 0,
        '&:hover': {
          backgroundColor:
            isFormValid && hasBudgetChanges
              ? theme.palette.primary.dark
              : theme.palette.grey[400],
        },
        '&:disabled': {
          backgroundColor: theme.palette.grey[400],
          color: theme.palette.primary.contrastText,
          opacity: 0.6,
        },
        '&:focus': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px',
        },
      }}
    >
      {updateDrawRequestMutation.isPending ||
      addAttachmentMutation.isPending ||
      deleteAttachmentMutation.isPending
        ? 'Updating...'
        : 'Update Draw Request'}
    </Button>
  );

  const renderFileSelector = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <FileSelector
        selectedFiles={selectedFiles}
        onFilesChange={handleFilesChangeWithRealtime}
        maxFiles={MAX_FILE_COUNT}
        maxFileSize={MAX_FILE_SIZE_MB}
        acceptedFileTypes={ACCEPTED_FILE_EXTENSIONS}
        buttonText="View/Attach Files"
        showChips={true}
        maxChipsVisible={0}
        disableSpacer={true}
        iconOnly={isMobile}
        allowDeletion={canDeleteAttachments}
        disabled={isReadOnly}
        onFileClick={handleAttachmentClick}
        sourceRecId={sourceRecId}
      />
    </Box>
  );

  const handleAttachmentClick = async (file: File) => {
    const attachmentId = (file as any).attachmentId;
    const fileName = (file as any).description || file.name;

    if (attachmentId && sourceRecId) {
      try {
        await useAttachmentApi.downloadAttachment(
          attachmentId,
          sourceRecId,
          fileName
        );
      } catch (error: any) {
        showCustomErrorToast({
          message: error?.message || 'Failed to download attachment',
        });
      }
    }
  };

  const handleMessagesClick = () => {
    if (sourceRecId && drawRequestId) {
      markMessagesAsReadMutation.mutate(
        { drawRequestId, sourceRecId },
        {
          onSuccess: () => {
            refetchMessages().then(() => {
              navigate(
                `${PrivateRoutes.DRAW_REQUEST_MESSAGE}?drawRequestId=${drawRequestId}&sourceRecId=${sourceRecId}&account=${account}`,
                {
                  state: {
                    from: location.pathname,
                    expandedLoan: sourceRecId,
                  },
                }
              );
            });
          },
          onError: () => {
            navigate(
              `${PrivateRoutes.DRAW_REQUEST_MESSAGE}?drawRequestId=${drawRequestId}&sourceRecId=${sourceRecId}&account=${account}`,
              {
                state: {
                  from: location.pathname,
                  expandedLoan: sourceRecId,
                },
              }
            );
          },
        }
      );
    } else {
      navigate(
        `${PrivateRoutes.DRAW_REQUEST_MESSAGE}?drawRequestId=${drawRequestId}&sourceRecId=${sourceRecId}&account=${account}`,
        {
          state: {
            from: location.pathname,
            expandedLoan: sourceRecId,
          },
        }
      );
    }
  };

  const renderMessagesButton = () => (
    <Button
      variant="outlined"
      aria-label="View messages"
      onClick={handleMessagesClick}
      sx={{
        position: 'relative',
        minWidth: 48,
        width: 48,
        height: 48,
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.background.paper,
        p: 0,
        '&:hover': {
          backgroundColor: theme.palette.primary.light + '20',
          borderColor: theme.palette.primary.dark,
        },
        '&:focus': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px',
        },
      }}
    >
      <MailOutline sx={{ color: theme.palette.primary.main, fontSize: 24 }} />

      {hasUnreadMessages && (
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 10,
            height: 10,
            backgroundColor: theme.palette.primary.main,
            borderRadius: '50%',
            border: `2px solid ${theme.palette.background.paper}`,
            zIndex: 1,
          }}
          aria-label="Unread messages indicator"
        />
      )}
    </Button>
  );

  const renderMobileFooter = () => {
    const hasExceedingAmounts = budgetItems.some(
      item => (item.requestAmount || 0) > (item.remainingAmount || 0)
    );

    return (
      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          p: { xs: '12px 16px', md: 4.4 },
          borderRadius: '16px 16px 0 0',
          backgroundColor: 'white',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          zIndex: 1000,
          boxShadow: `0 -4px 20px ${colors.ui.shadow.medium}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Epilogue',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '1.4',
              color: theme.palette.text.primary,
            }}
            component="div"
            role="text"
            aria-label={`Draw total: $${drawTotal.toLocaleString()}`}
          >
            Draw Total = ${drawTotal.toLocaleString()}
          </Typography>
        </Box>

        {hasExceedingAmounts && (
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.5 }}
            role="alert"
            aria-live="polite"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <InfoIcon
                sx={{ fill: colors.orange[500], fontSize: '16px' }}
                aria-hidden="true"
              />
              <Typography
                variant="body3"
                sx={{
                  fontStyle: 'italic',
                  fontSize: '12px',
                }}
              >
                Alert: Budget item(s) exceed the allocated amount.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Third row: Three buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: isReadOnly ? 'flex-end' : 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {!isReadOnly && renderUpdateButton()}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {renderFileSelector()}
            {renderMessagesButton()}
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderDesktopHeader = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 2,
      }}
    >
      <Typography
        variant="h6"
        component="h2"
        sx={{
          width: '25%',
        }}
      >
        Loan Progress
      </Typography>
      <Box sx={{ width: '75%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FileSelector
            selectedFiles={selectedFiles}
            onFilesChange={handleFilesChangeWithRealtime}
            maxFiles={10}
            acceptedFileTypes={ACCEPTED_FILE_EXTENSIONS}
            buttonText="Attach Files"
            showChips={true}
            maxChipsVisible={2}
            allowDeletion={canDeleteAttachments}
            onFileClick={handleAttachmentClick}
            sourceRecId={sourceRecId}
            disabled={isReadOnly}
          />
        </Box>
      </Box>
    </Box>
  );

  const renderDesktopContent = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isSmallDesktop ? 'column' : 'row',
        gap: 3,
        alignItems: isSmallDesktop ? 'stretch' : 'flex-start',
      }}
    >
      <Box sx={{ flex: 1 }}>
        {isReadOnly ? (
          <BudgetGridReadOnlyView
            drawDetails={drawRequestDetails?.drawRequestDetails || []}
            loanRecId={sourceRecId || ''}
            title="Budget Details"
          />
        ) : (
          <BudgetGridEditView
            sourceRecId={sourceRecId || ''}
            initialItems={budgetItems}
            onItemsChange={handleBudgetItemsChange}
            onValidationChange={handleValidationChange}
            showSubmitButton={true}
            isFormValid={isFormValid && hasBudgetChanges}
            onSubmitRequest={handleSubmitRequest}
            isSubmitting={
              updateDrawRequestMutation.isPending ||
              addAttachmentMutation.isPending ||
              deleteAttachmentMutation.isPending
            }
            buttonText="Update Draw Request"
            submittingText="Updating..."
          />
        )}
      </Box>
      <Box sx={{ width: isSmallDesktop ? '100%' : 400 }}>
        {drawRequestId && sourceRecId && (
          <DrawRequestMessage
            drawRequestId={drawRequestId}
            sourceRecId={sourceRecId}
            account={account}
          />
        )}
      </Box>
    </Box>
  );

  const renderDesktopTabletLayout = () => (
    <Paper
      elevation={0}
      sx={{
        p: { xs: '22px 18px', md: 4.4 },
        backgroundColor: theme.palette.background.paper,
        width: '100%',
        mb: 4,
      }}
      component="section"
      aria-label="Desktop layout content"
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {renderDesktopHeader()}
        <LoanProgress
          sourceRecId={sourceRecId || ''}
          budgetItems={budgetItems}
          isDisbursed={isDisbursed}
        />
        {renderDesktopContent()}
      </Box>
    </Paper>
  );

  const handleBackClick = useCallback(() => {
    if (isNavigating) {
      return;
    }

    setIsNavigating(true);

    const fromPath = location.state?.from;
    const expandedLoan = location.state?.expandedLoan;

    const isValidFromPath =
      fromPath &&
      fromPath !== location.pathname &&
      !fromPath.includes(PrivateRoutes.REQUEST_DRAW.split(':')[0]);

    if (isValidFromPath && fromPath !== PrivateRoutes.ROOT) {
      if (fromPath.includes(PrivateRoutes.LOAN_DETAILS.split(':')[0])) {
        navigate(fromPath, {
          state: {
            expandedLoan: expandedLoan,
            scrollTo: 'my-draws-section',
          },
        });
      } else {
        navigate(fromPath, {
          state: {
            expandedLoan: expandedLoan,
          },
        });
      }
    } else {
      clearRouteHistory();
      setIntentionalPath(PrivateRoutes.ROOT);
      navigate(PrivateRoutes.ROOT, {
        replace: true,
        state: {
          scrollTo: 'your-loans-tile',
          expandedLoan: sourceRecId || expandedLoan,
        },
      });
    }

    setTimeout(() => {
      setIsNavigating(false);
    }, 1000);
  }, [
    isNavigating,
    location.state,
    location.pathname,
    navigate,
    account,
    setIntentionalPath,
  ]);

  const renderBackButton = () => (
    <Box sx={{ mt: 8, mb: 3 }}>
      <Button
        onClick={handleBackClick}
        className="back-button"
        aria-label="Go back to previous page"
        sx={{
          '&:disabled': {
            opacity: 0.7,
            cursor: 'not-allowed',
          },
          '&:focus': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '2px',
          },
        }}
      >
        {'< Back'}
      </Button>
    </Box>
  );

  const renderPageHeader = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 4,
      }}
    >
      <Typography variant="pageTitle" component="h1">
        {isReadOnly ? 'Draw Details' : 'Review Draw Request'}{' '}
        {drawRequestDetails?.drawRequestId &&
          `#${drawRequestDetails.drawRequestId}`}
      </Typography>
      {drawRequestDetails && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Chip
            label={getStatusChipColor(drawRequestDetails.status, theme).label}
            sx={{
              backgroundColor: getStatusChipColor(
                drawRequestDetails.status,
                theme
              ).bgColor,
              color: getStatusChipColor(drawRequestDetails.status, theme)
                .textColor,
              borderRadius: getStatusChipColor(drawRequestDetails.status, theme)
                .borderRadius,
              fontSize: '14px',
              height: '32px',
              fontWeight: 500,
            }}
            size="medium"
            role="status"
            aria-label={`Draw request status: ${getStatusChipColor(drawRequestDetails.status, theme).label}`}
          />
        </Box>
      )}
    </Box>
  );

  const renderLoadingState = () => (
    <Box
      sx={{ display: 'flex', justifyContent: 'center', p: 4 }}
      role="status"
      aria-live="polite"
    >
      <Typography variant="body1">Loading draw request details...</Typography>
    </Box>
  );

  const renderErrorState = () => (
    <Box
      sx={{ display: 'flex', justifyContent: 'center', p: 4 }}
      role="alert"
      aria-live="polite"
    >
      <Typography color="error">
        Error loading draw request:{' '}
        {drawRequestError?.message || 'Unknown error'}
      </Typography>
    </Box>
  );

  const renderContent = () => {
    if (isLoadingDrawRequest || isLoadingAttachments) {
      return renderLoadingState();
    }

    if (drawRequestError) {
      return renderErrorState();
    }

    return !isMobile ? renderDesktopTabletLayout() : renderMobileLayout();
  };

  const renderMainContent = () => (
    <Box
      sx={{
        mx: 'auto',
        backgroundColor: theme.palette.background.footer,
        pb: isMobile || isTablet ? '220px' : 4,
        p: 2,
        pl: { xs: 2, sm: 2, md: 4 },
        pr: { xs: 2, sm: 2, md: 4 },
      }}
      component="main"
      role="main"
      aria-label={isReadOnly ? 'Draw details page' : 'Review draw request page'}
    >
      {renderBackButton()}
      {renderPageHeader()}
      {renderContent()}
      {isMobile && renderMobileFooter()}
    </Box>
  );

  return renderMainContent();
}
